import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, SignalWifiOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axios';
import { LoginResponse, PointOfSale } from '../types/types';
import { t } from '../utils/translations';
import PhoneInput from '../components/PhoneInput';

// Connection error event name
const CONNECTION_ERROR_EVENT = 'connection-error-state';

// Create a custom event for broadcasting connection errors
export const broadcastConnectionError = (isError: boolean, countdown: number = 5) => {
  const event = new CustomEvent(CONNECTION_ERROR_EVENT, {
    detail: { isError, countdown }
  });
  window.dispatchEvent(event);
};

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPosDialog, setShowPosDialog] = useState(false);
  const [loginResponse, setLoginResponse] = useState<LoginResponse | null>(null);
  const [step, setStep] = useState<'phone' | 'password' | 'register'>('phone');

  // Connection error state
  const [showConnectionError, setShowConnectionError] = useState(false);
  const [connectionErrorCountdown, setConnectionErrorCountdown] = useState(5);
  const [countdownTimer, setCountdownTimer] = useState<NodeJS.Timeout | null>(null);

  // Listen for connection error events
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    
    const handleConnectionError = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { isError, countdown } = customEvent.detail;
      
      setShowConnectionError(isError);
      setConnectionErrorCountdown(countdown);
      
      // Clear any existing timer first
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      
      if (isError && countdown === 5) {
        // Start the countdown timer
        timer = setInterval(() => {
          setConnectionErrorCountdown(prev => {
            const newCount = prev - 1;
            if (newCount <= 0) {
              if (timer) {
                clearInterval(timer);
                timer = null;
              }
              // Reset the form and clear error after countdown finishes
              setShowConnectionError(false);
              setStep('phone');
              setPhoneNumber('');
              setPassword('');
              setFirstName('');
              setLastName('');
              setError('');
            }
            return newCount;
          });
        }, 1000);
        
        setCountdownTimer(timer);
      }
    };

    window.addEventListener(CONNECTION_ERROR_EVENT, handleConnectionError);
    
    return () => {
      window.removeEventListener(CONNECTION_ERROR_EVENT, handleConnectionError);
      // Clear any existing timer when the component unmounts
      if (timer) {
        clearInterval(timer);
      }
    };
  }, []); // No dependencies needed anymore

  const handleConnectionErrorRetry = () => {
    // Clear the timer
    if (countdownTimer) {
      clearInterval(countdownTimer);
      setCountdownTimer(null);
    }
    
    setShowConnectionError(false);
    window.location.reload();
  };

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumber(value);
  };

  const handleCheckPhone = async () => {
    setError('');
    setIsLoading(true);

    try {
      const response = await axiosInstance.post('/api/v1/auth/check-phone-number/', {
        phone_number: phoneNumber,
      });

      setStep(response.data.has_password ? 'password' : 'register');
    } catch (error: any) {
      setError(t(error.response?.data?.error) || t('An error occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async () => {
    setError('');
    setIsLoading(true);

    try {
      const response = await axiosInstance.post('/api/v1/auth/login/', {
        phone_number: phoneNumber,
        password: password,
      });

      if (response.data) {
        // Check if points_of_sale array is empty
        if (response.data.points_of_sale.length === 0) {
          setLoginResponse(response.data);
          setError(t('Your account is not connected to any point of sale. Please contact the administrator.'));
        } else if (response.data.points_of_sale.length === 1) {
          login(response.data);
          navigate('/requests');
        } else {
          setLoginResponse(response.data);
          setShowPosDialog(true);
        }
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || t('Invalid credentials'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    setError('');
    setIsLoading(true);

    try {
      await axiosInstance.post('/api/v1/auth/register/', {
        phone_number: phoneNumber,
        password: password,
        first_name: firstName,
        last_name: lastName,
      });

      // After successful registration, proceed with login
      handleLogin();
    } catch (error: any) {
      setError(error.response?.data?.detail || t('Registration failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePosSelect = (pos: PointOfSale) => {
    if (loginResponse) {
      login(loginResponse);
      setShowPosDialog(false);
      navigate('/requests');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (step === 'phone') {
      handleCheckPhone();
    } else if (step === 'password') {
      handleLogin();
    } else if (step === 'register') {
      handleRegister();
    }
  };

  // Check if phone number is valid (has 10 digits for Kazakhstan)
  const isValidPhoneNumber = (phone: string): boolean => {
    // Should match +7XXXXXXXXXX format (10 digits after country code)
    return /^\+7\d{10}$/.test(phone);
  };

  // Render connection error UI
  const renderConnectionError = () => {
    return (
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          textAlign: 'center'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(236, 36, 143, 0.1)',
              color: '#ec248f',
            }}
          >
            <SignalWifiOff fontSize="large" />
          </Box>
        </Box>
        
        <Typography variant="h5" gutterBottom fontWeight="bold" color="error.main">
          {t('Connection Error')}
        </Typography>
        
        <Typography sx={{ mb: 4 }}>
          {t('Could not connect to the server. Please check your internet connection and try again.')}
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            {t('Redirecting to login page in')}:
          </Typography>
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={(connectionErrorCountdown / 5) * 100}
              size={40}
              thickness={4}
              sx={{ color: '#ec248f' }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" component="div" fontWeight="bold">
                {connectionErrorCountdown}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        <Button
          variant="contained"
          onClick={handleConnectionErrorRetry}
          sx={{ 
            borderRadius: 2,
            px: 4,
            py: 1,
            background: 'linear-gradient(45deg, #ec248f 30%, #ff6eb5 90%)',
            boxShadow: '0 4px 10px rgba(236, 36, 143, 0.25)',
            '&:hover': {
              boxShadow: '0 6px 12px rgba(236, 36, 143, 0.35)',
            }
          }}
        >
          {t('Try Again')}
        </Button>
      </Paper>
    );
  };

  // Render no point of sale error screen
  const renderNoPointOfSaleError = () => {
    return (
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 3, sm: 4 },
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          textAlign: 'center'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(236, 36, 143, 0.1)',
              color: '#ec248f',
            }}
          >
            <SignalWifiOff fontSize="large" />
          </Box>
        </Box>
        
        <Typography variant="h5" gutterBottom fontWeight="bold" color="error.main">
          {t('Account Not Connected')}
        </Typography>
        
        <Typography sx={{ mb: 4 }}>
          {t('Your account is not connected to any point of sale. Please contact the administrator.')}
        </Typography>
        
        <Button
          variant="contained"
          onClick={() => {
            setStep('phone');
            setLoginResponse(null);
            setError('');
          }}
          sx={{ 
            borderRadius: 2,
            px: 4,
            py: 1,
            background: 'linear-gradient(45deg, #ec248f 30%, #ff6eb5 90%)',
            boxShadow: '0 4px 10px rgba(236, 36, 143, 0.25)',
            '&:hover': {
              boxShadow: '0 6px 12px rgba(236, 36, 143, 0.35)',
            }
          }}
        >
          {t('Back to Phone Number')}
        </Button>
      </Paper>
    );
  };

  const renderStep = () => {
    switch (step) {
      case 'phone':
        return (
          <>
            <PhoneInput
              fullWidth
              label={t('Phone Number')}
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              margin="normal"
              required
              error={!!error}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading || !isValidPhoneNumber(phoneNumber)}
              sx={{ 
                mt: 3,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #ec248f 30%, #ff6eb5 90%)',
                boxShadow: '0 4px 10px rgba(236, 36, 143, 0.25)',
                '&:hover': {
                  boxShadow: '0 6px 12px rgba(236, 36, 143, 0.35)',
                }
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : t('Continue')}
            </Button>
          </>
        );

      case 'password':
        return (
          <>
            <TextField
              fullWidth
              label={t('Password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              error={!!error}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton 
                      onClick={() => setShowPassword(!showPassword)} 
                      edge="end"
                      type="button"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{ mt: 3 }}
            >
              {isLoading ? <CircularProgress size={24} /> : t('Login')}
            </Button>
          </>
        );

      case 'register':
        return (
          <>
            <TextField
              fullWidth
              label={t('First Name')}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t('Last Name')}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label={t('Password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              error={!!error}
              helperText={t('Password must be at least 8 characters')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              fullWidth
              variant="contained"
              onClick={handleRegister}
              disabled={isLoading || password.length < 8}
              sx={{ mt: 3 }}
            >
              {isLoading ? <CircularProgress size={24} /> : t('Register')}
            </Button>
          </>
        );
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 64px - 56px)', // Adjust for header and footer
          px: { xs: 2, sm: 3 }
        }}
      >
        <Container maxWidth="sm" sx={{ width: '100%' }}>
          {showConnectionError ? (
            renderConnectionError()
          ) : loginResponse && loginResponse.points_of_sale.length === 0 ? (
            renderNoPointOfSaleError()
          ) : (
            <Paper 
              elevation={3} 
              sx={{ 
                p: { xs: 3, sm: 4 },
                borderRadius: 2,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
              }}
            >
              <Typography variant="h5" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
                {step === 'phone' ? t('Enter Phone Number') : 
                step === 'password' ? t('Enter Password') : t('Create Account')}
              </Typography>

              {error && (
                <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>
                  {error}
                </Typography>
              )}

              <Box 
                component="form" 
                onSubmit={handleSubmit}
                sx={{ mt: 2 }}
              >
                {renderStep()}
              </Box>

              {step !== 'phone' && (
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => setStep('phone')}
                  sx={{ mt: 2 }}
                  type="button"
                >
                  {t('Back to Phone Number')}
                </Button>
              )}
            </Paper>
          )}
        </Container>
      </Box>

      <Dialog open={showPosDialog} onClose={() => setShowPosDialog(false)}>
        <DialogTitle>{t('Select Point of Sale')}</DialogTitle>
        <DialogContent>
          <List>
            {loginResponse?.points_of_sale.map((pos) => (
              <ListItem key={pos.id} disablePadding>
                <ListItemButton onClick={() => handlePosSelect(pos)}>
                  <ListItemText 
                    primary={pos.name} 
                    secondary={`${pos.city} - ${t(pos.type)}`} 
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Login; 